import { useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import api from '../../../app/api';

const CODE_LENGTH = 6;

export function MfaCodeInput({ label, value = '', onChange }) {
  const inputRefs = useRef([]);
  const digits = String(value).padEnd(CODE_LENGTH, ' ').slice(0, CODE_LENGTH).split('');

  const handleChange = (event, index) => {
    const entered = event.target.value.replace(/\D/g, '');
    const nextDigits = [...digits];
    if (entered.length > 1) {
      const availableDigits = entered.slice(0, CODE_LENGTH - index);
      availableDigits.split('').forEach((digit, offset) => {
        nextDigits[index + offset] = digit;
      });
      onChange(nextDigits.join(''));
      inputRefs.current[Math.min(index + availableDigits.length, CODE_LENGTH - 1)]?.focus();
    } else {
      nextDigits[index] = entered || ' ';
      onChange(nextDigits.join(''));
      if (entered && index < CODE_LENGTH - 1) inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = event => {
    const pastedDigits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pastedDigits) return;
    event.preventDefault();
    onChange(pastedDigits.padEnd(CODE_LENGTH, ' '));
    inputRefs.current[Math.min(pastedDigits.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault();
      inputRefs.current[index + 1]?.focus();
    } else if (event.key === 'Backspace') {
      event.preventDefault();
      const nextDigits = [...digits];
      if (digits[index].trim()) {
        nextDigits[index] = ' ';
      } else if (index > 0) {
        nextDigits[index - 1] = ' ';
        inputRefs.current[index - 1]?.focus();
      }
      onChange(nextDigits.join(''));
    }
  };

  return (
    <div className="mfa-code-field" role="group" aria-label={label}>
      <Form.Label>{label}</Form.Label>
      <div className="mfa-code-inputs">
        {Array.from({ length: CODE_LENGTH }, (_, index) => (
          <Form.Control
            key={index}
            ref={element => { inputRefs.current[index] = element; }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
            value={digits[index].trim()}
            onChange={event => handleChange(event, index)}
            onPaste={handlePaste}
            onKeyDown={event => handleKeyDown(event, index)}
          />
        ))}
      </div>
    </div>
  );
}

const MfaVerificationModal = (props) => {
  const { show, setShow, mfaDetails } = props;
  const [verificationCode, setVerificationCode] = useState('');

  const handleVerifyMfa = async () => {
    await api.post(
      '/auth/mfa/verify',
      { 
        code: verificationCode.replace(/\D/g, '')
      },
      {
        headers: {
          'Authorization': `Bearer ${mfaDetails.mfaToken}`
        },
        withCredentials: true,
      }
    ).then((r) => {
      props.setLoading(true);
      setShow(false);
      setVerificationCode('');
      props.handleLoggedIn();
      props.handleEmailUpdate(r.data.email);
      props.handleNameUpdate(r.data.name);
      props.handleSurnameUpdate(r.data.surname);
      props.handleMfaEnabled(r.data.mfaEnabled);
      props.handleMfaType(r.data.mfaType);
      api.get('/api/profiles/data')
      .then((response) => {
        if (response.status === 200 && response.data.selectedProfile !== undefined) {
          props.handleSave(response.data);
          props.setLoading(false);
        } else {
          api.post('/api/profiles/data',
          {
            selectedProfile: {id:props.profile.id, name:props.profile.name, yearCalc: props.profile.yearCalc},
            income: props.income.records,
            expenses: props.expenses.records,
            debt: props.debt,
            descriptions: [...props.income.descriptions, ...props.expenses.descriptions, ...props.debt.descriptions],
            profiles: []
          }
        )
        .then((postResponse) => {
          props.handleSave(postResponse.data);
          props.setLoading(false);
        })
        .catch((error) => {
          props.setErrorTitle("Failed to get save data");
          props.setErrorMessage("An error accurred whilst saving your data. Please try again.");
          props.setErrorShow(true);
          props.setLoading(false);
        })
      }
      })
      .catch((error) => {
        props.setErrorTitle("Failed to get your data");
        props.setErrorMessage("An error accurred whilst fetching your data. Please try again.");
        props.setErrorShow(true);
        props.setLoading(false);
      });        
    })
    .catch((error) => {
      props.setErrorTitle(error.response?.status === 400 ? 'Invalid verification code' : 'Could not verify your code');
      props.setErrorMessage(error.response?.status === 400
        ? 'Check the code and try again.'
        : 'An error occurred while verifying your code. Please try again.');
      props.setErrorShow(true);
    });
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Multi-factor authentication</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={event => {
          event.preventDefault();
          if (verificationCode.replace(/\D/g, '').length === 6) handleVerifyMfa();
        }}>
          <MfaCodeInput
            label={`Code from your ${mfaDetails.mfaType === 'TOTP' ? 'authenticator app' : 'email'}`}
            value={verificationCode}
            onChange={setVerificationCode}
          />
          <Button className="mt-3" type="submit" variant="primary" disabled={verificationCode.replace(/\D/g, '').length !== 6}>
            Verify code
          </Button>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default MfaVerificationModal;